import React, { useEffect, useState, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import axios from 'axios';
import { format } from 'date-fns';
import './DtmTrendsChart.css';

const formatNumber = (value) => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(0)}K`;
  }
  return value;
};

function DtmTrendsChart({ view, selectedLocation, showOnlyBadge = false }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const chartRef = useRef(null);
  const prevDataRef = useRef([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!view) {
        setData([]);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.get(`/api/dtm/totals-by-round`, {
          params: { 
            view,
            admin1Pcode: selectedLocation?.pcode
          }
        });

        const chartData = response.data.map(item => ({
          date: format(new Date(item.reporting_date), 'MMM yyyy'),
          fullDate: format(new Date(item.reporting_date), 'dd MMM yyyy'),
          value: item.total_idps,
          round: item.round_number
        }));

        prevDataRef.current = data;
        setData(chartData);

        if (chartRef.current && !showOnlyBadge) {
          const chartInstance = chartRef.current.getEchartsInstance();
          chartInstance.setOption({
            series: [{
              data: chartData.map(d => d.value),
              animation: true,
              animationDuration: 1000,
              animationEasing: 'cubicInOut'
            }]
          });
        }
      } catch (err) {
        console.error('Error fetching DTM data:', err);
        setError('Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [view, selectedLocation, showOnlyBadge]);

  if (loading && !data.length) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!data.length) return null;

  const latestData = data[data.length - 1];

  if (showOnlyBadge && data.length > 0) {
    return (
      <div className="dtm-trends-chart-summary-badge">
        <div className="badge-item">
          <span className="label">Round</span>
          <span className="value">{latestData.round}</span>
        </div>
        <div className="divider"></div>
        <div className="badge-item">
          <span className="label">Date</span>
          <span className="value">{latestData.fullDate}</span>
        </div>
        <div className="divider"></div>
        <div className="badge-item">
          <span className="label">Total IDPs</span>
          <span className="value">{formatNumber(latestData.value)}</span>
        </div>
      </div>
    );
  }

  const option = {
    animation: true,
    animationDuration: 1000,
    animationEasing: 'cubicInOut',
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: '#FFFFFF',
      borderWidth: 0,
      padding: [8, 10],
      extraCssText: 'border-radius: 4px; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);',
      textStyle: {
        color: 'rgba(0, 0, 0, 0.87)',
        fontSize: 'var(--font-size-sm)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        lineHeight: 'var(--line-height-normal)'
      },
      position: function (point, params, dom, rect, size) {
        const rightEdgeDistance = size.viewSize[0] - point[0];
        const tooltipWidth = dom.offsetWidth;
        const x = rightEdgeDistance < tooltipWidth ? point[0] - tooltipWidth : point[0];
        return [Math.max(0, x), point[1]];
      },
      formatter: (params) => {
        const dataPoint = data[params[0].dataIndex];
        const value = params[0].value;
        let formattedValue;
        if (value >= 1000000) {
          const millions = value / 1000000;
          formattedValue = `${millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(1)}M`;
        } else if (value >= 1000) {
          formattedValue = `${(value / 1000).toFixed(0)}K`;
        } else {
          formattedValue = value.toString();
        }
        
        return `<div class="dtm-trends-chart-tooltip-content">
          <div class="dtm-trends-chart-tooltip-header">
            <span class="dtm-trends-chart-tooltip-badge">Round ${dataPoint.round}</span>
          </div>
          <div class="dtm-trends-chart-tooltip-body">
            <div class="dtm-trends-chart-tooltip-row">
              <span class="dtm-trends-chart-label">Date:</span>
              <span class="dtm-trends-chart-tooltip-value">${dataPoint.fullDate}</span>
            </div>
            <div class="dtm-trends-chart-tooltip-row">
              <span class="dtm-trends-chart-label">IDPs:</span>
              <span class="dtm-trends-chart-value-highlight">${formattedValue}</span>
            </div>
          </div>
        </div>`;
      }
    },
    textStyle: {
      fontFamily: 'var(--font-family-base)',
      fontSize: 'var(--font-size-md)',
      color: 'var(--color-text-primary)'
    },
    grid: {
      left: '50px',
      right: '20px',
      bottom: '20px',
      top: '4%',
      containLabel: false
    },
    xAxis: {
      type: 'category',
      data: data.map(item => item.date),
      axisLine: {
        lineStyle: { color: '#E0E0E0' }
      },
      axisTick: { show: false },
      axisLabel: {
        color: '#666666',
        fontSize: 'var(--font-size-md)',
        fontFamily: 'var(--font-family-base)'
      }
    },
    yAxis: {
      type: 'value',
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: {
        lineStyle: { color: '#E0E0E0', type: 'dashed' }
      },
      axisLabel: {
        color: '#666666',
        fontSize: 'var(--font-size-md)',
        fontFamily: 'var(--font-family-base)',
        formatter: (value) => {
          if (value >= 1000000) {
            return `${(value / 1000000)}M`;
          } else if (value >= 1000) {
            return `${(value / 1000)}K`;
          }
          return value;
        }
      }
    },
    series: [{
      data: data.map(item => item.value),
      type: 'line',
      smooth: true,
      symbol: 'circle',
      symbolSize: 6,
      itemStyle: {
        color: '#FFF',
        borderWidth: 2,
        borderColor: 'rgba(0, 109, 135, 1)'
      },
      lineStyle: {
        width: 2.4,
        color: 'rgba(0, 109, 135, 1)'
      },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [{
            offset: 0,
            color: 'rgba(0, 109, 135, 0.4)'
          }, {
            offset: 1,
            color: 'rgba(0, 109, 135, 0.1)'
          }]
        }
      }
    }]
  };

  return (
    <div style={{ width: '100%', position: 'relative', padding: '10px 15px', backgroundColor: 'var(--color-background-paper)', marginBottom: '16px' }}>
      <h3>DTM Displacement Data</h3>
      <br/>
      <ReactECharts
        ref={chartRef}
        option={option}
        style={{ height: '200px', width: '100%' }}
        className="dtm-trends-chart"
        notMerge={false}
        lazyUpdate={true}
      />
    </div>
  );
}

export default DtmTrendsChart;
